import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Eye, Pencil, RefreshCw, Link as LinkIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { JobCard, CreateJobCardRequest, Department } from '../types/jobcard';
import { getJobCards, createJobCard, deleteJobCard, getATRItems, syncAllJobCards } from '../services/jobcardApi';
import { getStores } from '../services/storeApi';
import { getUsers } from '../services/userApi';
import { getDepartments } from '../services/departmentApi';
// import JobCardList from '../components/JobCardList';
import CreateJobCardModal from '../components/modals/CreateJobCardModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { Store } from '../types/store';
import { User } from '../types/auth';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Could not find a declaration file for module 'xlsx'.
import * as XLSX from 'xlsx';
import { AssetToRepair } from '../types/jobcard';

// Add these constants at the top of the file, outside the component
const FILTER_STORAGE_KEY = 'jobCardsFilters';

const getInitialFilters = () => {
    const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
    if (savedFilters) {
        return JSON.parse(savedFilters);
    }
    return {
        searchTerm: '',
        statusFilter: 'open' as const,
        dateRangeFilter: {
            startDate: '',
            endDate: ''
        },
        storeFilter: 'all',
        userFilter: 'all',
        departmentFilter: 'all'
    };
};

const getInitialViewMode = () => {
    const saved = localStorage.getItem('jobCardsViewMode');
    if (saved === 'card' || saved === 'table') return saved as 'card' | 'table';

    // Detect mobile (e.g., width <= 768px)
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        return 'card';
    }
    return 'table';
};

export default function JobCardsPage() {
    // Destructure `user` to access the user type (utype) so we can conditionally render UI elements
    const { isAdmin, user } = useAuth();
    const [jobCards, setJobCards] = useState<JobCard[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [jobCardToDelete, setJobCardToDelete] = useState<JobCard | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Add pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Replace the filter state initializations with the saved values
    const [searchTerm, setSearchTerm] = useState(getInitialFilters().searchTerm);
    const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>(getInitialFilters().statusFilter);
    const [dateRangeFilter, setDateRangeFilter] = useState(getInitialFilters().dateRangeFilter);
    const [storeFilter, setStoreFilter] = useState(getInitialFilters().storeFilter);
    const [userFilter, setUserFilter] = useState(getInitialFilters().userFilter);
    const [departmentFilter, setDepartmentFilter] = useState(getInitialFilters().departmentFilter);
    const [stores, setStores] = useState<Store[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);

    // Add state to track job cards with ATR items
    const [jobCardsWithATRItems, setJobCardsWithATRItems] = useState<Record<number, boolean>>({});

    // Add state for view mode
    const [viewMode, setViewMode] = useState<'table' | 'card'>(getInitialViewMode);

    // Add state for syncing
    const [isSyncing, setIsSyncing] = useState(false);

    // Format date as dd-MMM-yyyy; returns 'N/A' when invalid or missing
    const formatDate = (value?: string | null) => {
        if (!value) return 'N/A';
        const d = new Date(value);
        if (isNaN(d.getTime())) return 'N/A';
        const day = String(d.getDate()).padStart(2, '0');
        const month = d.toLocaleString('en-GB', { month: 'short' }); // Jan, Feb, Mar
        const year = String(d.getFullYear());
        return `${day}-${month}-${year}`;
    };

    // Format date closed as '13-May-2025 11:46:15 AM'; returns 'N/A' when invalid or missing
    const formatDateClosed = (value?: string | null) => {
        if (!value) return 'N/A';
        const d = new Date(value);
        if (isNaN(d.getTime())) return 'N/A';
        const day = String(d.getDate()).padStart(2, '0');
        const month = d.toLocaleString('en-GB', { month: 'short' }); // Jan, Feb, Mar
        const year = String(d.getFullYear());
        const time = d.toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: 'numeric', 
            minute: '2-digit', 
            second: '2-digit' 
        });
        return `${day}-${month}-${year} ${time}`;
    };

    // Function to handle URL parameters and update filters
    const handleURLParameters = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlUserId = searchParams.get('user_id');
        const urlStartDate = searchParams.get('start_date');
        const urlEndDate = searchParams.get('end_date');
        const urlDateRange = searchParams.get('date_range');

        // If URL parameters exist, update the filters
        if (urlUserId || urlStartDate || urlEndDate || urlDateRange) {
            const updatedFilters = {
                searchTerm: '',
                statusFilter: 'all' as const,
                dateRangeFilter: {
                    startDate: urlStartDate || '',
                    endDate: urlEndDate || ''
                },
                storeFilter: 'all',
                userFilter: urlUserId || 'all',
                departmentFilter: 'all'
            };

            // Update state
            setSearchTerm(updatedFilters.searchTerm);
            setStatusFilter(updatedFilters.statusFilter);
            setDateRangeFilter(updatedFilters.dateRangeFilter);
            setStoreFilter(updatedFilters.storeFilter);
            setUserFilter(updatedFilters.userFilter);
            setDepartmentFilter(updatedFilters.departmentFilter);

            // Save to localStorage
            localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(updatedFilters));

            // Clear URL parameters after processing
            navigate('/jobcards', { replace: true });
        }
    }, [location.search, navigate]);
    


    // Add state for filters visibility
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

    // Handle URL parameters on component mount and location changes
    useEffect(() => {
        handleURLParameters();
    }, [handleURLParameters]);

    // Add this effect to save filters when they change
    useEffect(() => {
        const filtersToSave = {
            searchTerm,
            statusFilter,
            dateRangeFilter,
            storeFilter,
            userFilter,
            departmentFilter
        };
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    }, [searchTerm, statusFilter, dateRangeFilter, storeFilter, userFilter, departmentFilter]);

    // Modify the resetFilters function to also clear localStorage
    const resetFilters = () => {
        const defaultFilters = {
            searchTerm: '',
            statusFilter: 'open' as const,
            dateRangeFilter: { startDate: '', endDate: '' },
            storeFilter: 'all',
            userFilter: 'all',
            departmentFilter: 'all'
        };

        setSearchTerm(defaultFilters.searchTerm);
        setStatusFilter(defaultFilters.statusFilter);
        setDateRangeFilter(defaultFilters.dateRangeFilter);
        setStoreFilter(defaultFilters.storeFilter);
        setUserFilter(defaultFilters.userFilter);
        setDepartmentFilter(defaultFilters.departmentFilter);
        setCurrentPage(1);

        localStorage.removeItem(FILTER_STORAGE_KEY);
    };

    const fetchJobCards = useCallback(async () => {
        try {
            setIsLoading(true);

            // Determine closed status more explicitly
            const closedStatus =
                statusFilter === 'closed' ? true :
                    statusFilter === 'open' ? false :
                        undefined;

            const response = await getJobCards(
                searchTerm,
                closedStatus,
                itemsPerPage,
                {
                    start_date: dateRangeFilter.startDate,
                    end_date: dateRangeFilter.endDate,
                    store_id: storeFilter !== 'all' ? storeFilter : undefined,
                    user_id: userFilter !== 'all' ? userFilter : undefined,
                    department_id: departmentFilter !== 'all' ? departmentFilter : undefined,
                    page: currentPage
                }
            );

            // Extract job cards and pagination data from nested response
            const paginatedData = response.data?.data;
            let loadedJobCards: JobCard[] = [];

            if (paginatedData && 'data' in paginatedData) {
                loadedJobCards = paginatedData.data;
                setJobCards(loadedJobCards);
                setTotalPages(paginatedData.last_page);
                setTotalItems(paginatedData.total);
                setCurrentPage(paginatedData.current_page);
            } else {
                loadedJobCards = paginatedData || [];
                setJobCards(loadedJobCards);
                setTotalPages(1);
                setTotalItems(loadedJobCards.length || 0);
                setCurrentPage(1);
            }

            // Check ATR items for each job card
            checkJobCardsForATRItems(loadedJobCards);
        } catch {
            showErrorToast('Failed to fetch job cards');
            setError('Failed to fetch job cards');
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, statusFilter, dateRangeFilter, storeFilter, userFilter, departmentFilter, itemsPerPage, currentPage]);

    // Add a function to check all job cards for ATR items
    const checkJobCardsForATRItems = async (cards: JobCard[]) => {
        try {
            // Check ATR items for all job cards, not just open ones
            const atrChecks = await Promise.allSettled(
                cards.map(async (card) => {
                    try {
                        const response = await getATRItems(card.id);
                        const atrItems = response.data.data?.data || [];
                        return {
                            id: card.id,
                            hasATRItems: Array.isArray(atrItems) && atrItems.length > 0
                        };
                    } catch (error) {
                        console.error(`Error checking ATR items for job card ${card.id}:`, error);
                        return { id: card.id, hasATRItems: false };
                    }
                })
            );

            // Create an object mapping job card IDs to their ATR status
            const jobCardsWithATR = atrChecks.reduce((acc, result) => {
                if (result.status === 'fulfilled') {
                    acc[result.value.id] = result.value.hasATRItems;
                }
                return acc;
            }, {} as Record<number, boolean>);

            setJobCardsWithATRItems(jobCardsWithATR);
        } catch (error) {
            console.error('Error checking job cards for ATR items:', error);
        }
    };

    // Fetch additional data for filters
    const fetchFilterData = useCallback(async () => {
        try {
            const [storesResponse, usersResponse, departmentsResponse] = await Promise.all([
                getStores(),
                isAdmin ? getUsers() : Promise.resolve({ data: [] }),
                getDepartments()
            ]);
            setStores(storesResponse.data.data);
            setUsers(isAdmin ? usersResponse.data : []);
            setDepartments(departmentsResponse);
        } catch {
            showErrorToast('Failed to fetch filter data');
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchJobCards();
    }, [fetchJobCards]);

    useEffect(() => {
        fetchFilterData();
    }, [fetchFilterData]);

    const handleCreateJobCard = async (data: CreateJobCardRequest) => {
        try {
            const response = await createJobCard(data);
            showSuccessToast('Job card created successfully');
            // Handle both response formats
            const jobCard = 'data' in response.data ? response.data.data : response.data;
            return { data: { id: Number(jobCard.id) } };
        } catch (error: unknown) {
            console.error('Create Job Card Error:', error);
            const apiError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
            if (apiError.response?.data?.errors) {
                const errorMessages = Object.entries(apiError.response.data.errors)
                    .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
                    .join('\n');
                showErrorToast(errorMessages);
            } else {
                showErrorToast('Failed to create job card');
            }
            throw error;
        }
    };

    const confirmDelete = async () => {
        if (!jobCardToDelete) return;

        try {
            await deleteJobCard(jobCardToDelete.id);
            await fetchJobCards();
            setIsDeleteModalOpen(false);
            setJobCardToDelete(null);
            showSuccessToast('Job card deleted successfully');
        } catch {
            showErrorToast('Failed to delete job card');
            setError('Failed to delete job card');
        }
    };

    // Add pagination controls
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        localStorage.setItem('jobCardsViewMode', viewMode);
    }, [viewMode]);

    const exportToExcel = async () => {
        try {
            setIsLoading(true);
            // Fetch all filtered job cards (not just current page)
            const closedStatus =
                statusFilter === 'closed' ? true :
                    statusFilter === 'open' ? false :
                        undefined;
            let page = 1;
            let allJobCards: JobCard[] = [];
            let lastPage = 1;
            do {
                const response = await getJobCards(
                    searchTerm,
                    closedStatus,
                    100, // fetch 100 per page for efficiency
                    {
                        start_date: dateRangeFilter.startDate,
                        end_date: dateRangeFilter.endDate,
                        store_id: storeFilter !== 'all' ? storeFilter : undefined,
                        user_id: userFilter !== 'all' ? userFilter : undefined,
                        department_id: departmentFilter !== 'all' ? departmentFilter : undefined,
                        page
                    }
                );
                const paginatedData = response.data?.data;
                let loadedJobCards: JobCard[] = [];
                if (paginatedData && 'data' in paginatedData) {
                    loadedJobCards = paginatedData.data;
                    lastPage = paginatedData.last_page;
                } else {
                    loadedJobCards = paginatedData || [];
                    lastPage = 1;
                }
                allJobCards = allJobCards.concat(loadedJobCards);
                page++;
            } while (page <= lastPage);

            // Fetch detailed ATR items for all job cards
            const atrItemsResults = await Promise.all(
                allJobCards.map(async (jc) => {
                    try {
                        const response = await getATRItems(jc.id);
                        const atrItems = response.data.data?.data || [];
                        return { 
                            id: jc.id, 
                            hasATR: Array.isArray(atrItems) && atrItems.length > 0,
                            atrItems: Array.isArray(atrItems) ? atrItems : []
                        };
                    } catch {
                        return { id: jc.id, hasATR: false, atrItems: [] };
                    }
                })
            );
            
            // Create a map for easy lookup
            const atrItemsMap: Record<number, { hasATR: boolean; atrItems: AssetToRepair[] }> = {};
            atrItemsResults.forEach(res => { 
                atrItemsMap[res.id] = { hasATR: res.hasATR, atrItems: res.atrItems }; 
            });

            // Prepare data for Excel with detailed ATR information
            const data = allJobCards.map(jc => {
                const atrData = atrItemsMap[jc.id] || { hasATR: false, atrItems: [] };
                
                // Format ATR items details into a single readable string
                let atrDetails = '';
                if (atrData.hasATR && atrData.atrItems.length > 0) {
                    atrDetails = atrData.atrItems.map((item, index: number) => {
                        const parts = [];
                        
                        // Add item number for multiple items
                        if (atrData.atrItems.length > 1) {
                            parts.push(`Item ${index + 1}:`);
                        }
                        
                        // Equipment description - use item.asset as primary identifier (matches what's shown in edit page)
                        parts.push(`Equipment: ${item.asset}`);
                        
                        // Serial number
                        if (item.equipment?.serial_number) {
                            parts.push(`Serial: ${item.equipment.serial_number}`);
                        }
                        
                        // Model number
                        if (item.equipment?.model_number) {
                            parts.push(`Model: ${item.equipment.model_number}`);
                        }
                        
                        // Equipment description if different from asset name
                        if (item.equipment?.description && item.equipment.description !== item.asset) {
                            parts.push(`Description: ${item.equipment.description}`);
                        }
                        
                        // Status
                        parts.push(`Status: ${item.completed ? 'Completed' : 'Pending'}`);
                        
                        // Add repair descriptions if available
                        if (item.descriptions && item.descriptions.length > 0) {
                            const repairDesc = item.descriptions.map((desc: { id: number, description: string }) => desc.description).join(', ');
                            parts.push(`Repairs: ${repairDesc}`);
                        }
                        
                        return parts.join(' | ');
                    }).join('\n\n'); // Use double newline to separate multiple items
                }

                return {
                    'Job Number': jc.job_number,
                    'Order Number': jc.order_number,
                    'Store': jc.store?.name,
                    'Store Number': jc.store?.storenumber,
                    'Date': jc.date,
                    'Time In': jc.time_in,
                    'Time Out': jc.time_out,
                    'Assigned To': jc.user?.name,
                    'Status': jc.closed ? 'Closed' : 'Open',
                    'Date Closed': formatDateClosed(jc.date_closed),
                    'ATR Items Details': atrDetails || 'No ATR Items'
                };
            });
            const ws = XLSX.utils.json_to_sheet(data);
            
            // Set column widths and enable text wrapping
            const columnWidths = [
                { wch: 15 }, // Job Number
                { wch: 15 }, // Order Number  
                { wch: 20 }, // Store
                { wch: 12 }, // Store Number
                { wch: 12 }, // Date
                { wch: 10 }, // Time In
                { wch: 10 }, // Time Out
                { wch: 20 }, // Assigned To
                { wch: 10 }, // Status
                { wch: 12 }, // Date Closed
                { wch: 50 }  // ATR Items Details (wider for long content)
            ];
            ws['!cols'] = columnWidths;
            
            // Enable text wrapping for all cells
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
            for (let R = range.s.r; R <= range.e.r; ++R) {
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                    if (!ws[cellAddress]) continue;
                    
                    // Initialize cell style if it doesn't exist
                    if (!ws[cellAddress].s) ws[cellAddress].s = {};
                    
                    // Enable text wrapping
                    ws[cellAddress].s.alignment = { 
                        wrapText: true, 
                        vertical: 'top' 
                    };
                    
                    // Set row height for rows with ATR data (column index 10 = ATR Items Details)
                    if (C === 10 && ws[cellAddress].v && ws[cellAddress].v.toString().length > 50) {
                        if (!ws['!rows']) ws['!rows'] = [];
                        if (!ws['!rows'][R]) ws['!rows'][R] = {};
                        ws['!rows'][R].hpt = 60; // Set height in points
                    }
                }
            }
            
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'JobCards');
            XLSX.writeFile(wb, 'jobcards_export.xlsx');
        } catch {
            showErrorToast('Failed to export job cards');
        } finally {
            setIsLoading(false);
        }
    };

    // Add sync all job cards function
    const handleSyncAll = async () => {
        try {
            setIsSyncing(true);
            const response = await syncAllJobCards();
            
            // Handle the enhanced response format
            if (response.data.success) {
                const { syncedCount, updatedCount, deletedCount, warnings, errors } = response.data;
                
                let message = `Sync completed: ${syncedCount} synced`;
                if (updatedCount > 0) message += `, ${updatedCount} updated`;
                if (deletedCount > 0) message += `, ${deletedCount} deleted`;
                if (warnings && warnings.length > 0) message += `, ${warnings.length} warnings`;
                
                showSuccessToast(message);
                
                // Show warnings if any
                if (warnings && warnings.length > 0) {
                    console.warn('Sync warnings:', warnings);
                    // You could show warnings in a more user-friendly way here
                }
                
                // Show errors if any
                if (errors && errors.length > 0) {
                    console.error('Sync errors:', errors);
                    showErrorToast(`${errors.length} errors occurred during sync`);
                }
            } else {
                showErrorToast(response.data.message || 'Failed to synchronize job cards');
            }
            
            await fetchJobCards(); // Refresh the job cards list after sync
        } catch (error) {
            console.error('Sync Error:', error);
            showErrorToast('Failed to synchronize job cards');
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Job Cards</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setViewMode('table')}
                        className={`px-3 py-1 rounded ${viewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                    >
                        Table
                    </button>
                    <button
                        onClick={() => setViewMode('card')}
                        className={`px-3 py-1 rounded ${viewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                    >
                        Cards
                    </button>
                    {user?.utype !== 'STORE' && (
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                        >
                            <Plus size={20} />
                            <span>New Job Card</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Filtering Section */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                >
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
                    <button className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                        {isFiltersExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        )}
                    </button>
                </div>
                {isFiltersExpanded && (
                    <div className="px-4 pb-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <input
                        type="text"
                        placeholder="Search job cards..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value as 'all' | 'open' | 'closed');
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="all">All Status</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>
                    <input
                        type="date"
                        value={dateRangeFilter.startDate}
                        onChange={(e) => {
                            setDateRangeFilter((prev: typeof dateRangeFilter) => ({ ...prev, startDate: e.target.value }));
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <input
                        type="date"
                        value={dateRangeFilter.endDate}
                        onChange={(e) => {
                            setDateRangeFilter((prev: typeof dateRangeFilter) => ({ ...prev, endDate: e.target.value }));
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <select
                        value={storeFilter}
                        onChange={(e) => {
                            setStoreFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="all">All Stores</option>
                        {stores.map(store => (
                            <option key={store.id} value={store.id}>
                                {store.name}
                            </option>
                        ))}
                    </select>
                    {isAdmin && (
                        <select
                            value={userFilter}
                            onChange={(e) => {
                                setUserFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="all">All Users</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <select
                        value={departmentFilter}
                        onChange={(e) => {
                            setDepartmentFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="all">All Departments</option>
                        {departments.map(department => (
                            <option key={department.id} value={department.id}>
                                {department.name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={resetFilters}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 border border-gray-300 dark:border-gray-600 shadow-sm hover:shadow"
                    >
                        <X size={16} className="text-gray-600 dark:text-gray-400" />
                        <span>Clear Filters</span>
                    </button>
                    <button
                        onClick={handleSyncAll}
                        className={`flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors duration-200 border shadow-sm ${
                            isSyncing 
                                ? 'bg-blue-400 border-blue-500 cursor-not-allowed opacity-70' 
                                : 'bg-blue-600 hover:bg-blue-700 border-blue-700 hover:shadow'
                        }`}
                        type="button"
                        disabled={isSyncing}
                    >
                        <RefreshCw size={16} className={`${isSyncing ? 'animate-spin' : ''}`} />
                        {isSyncing ? 'Syncing...' : 'Sync All'}
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors duration-200 border border-green-700 shadow-sm hover:shadow"
                        type="button"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="inline-block">
                            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M7 11l5 5m0 0l5-5m-5 5V4" />
                        </svg>
                        Export to Excel
                    </button>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/50 rounded-lg">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center min-h-[400px] bg-white dark:bg-gray-800 rounded-lg shadow">
                    <div className="flex flex-col items-center space-y-3">
                        <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin"></div>
                        <p className="text-gray-600 dark:text-gray-400">Loading job cards...</p>
                    </div>
                </div>
            ) : (
                <>
                    {viewMode === 'table' ? (
                        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-700">
                                        <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Actions
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Job Number
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Store / Supplier
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Created At
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Assigned To
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {jobCards.map((jobCard) => (
                                        <tr key={jobCard.id}
                                            className={`
                                                ${jobCard.closed 
                                                    ? 'bg-green-100 dark:bg-green-800/30 hover:bg-green-200 dark:hover:bg-green-800/50' 
                                                    : 'bg-yellow-100 dark:bg-yellow-800/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50'
                                                } 
                                                transition-colors
                                            `}
                                        >
                                            <td className="px-3 py-4 text-center">
                                                <div className="flex justify-center gap-1">
                                                    {jobCard.closed ? (
                                                        <button
                                                            onClick={() => navigate(`/jobcards/view/${jobCard.id}`)}
                                                            className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                            title="View Job Card"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => navigate(`/jobcards/edit/${jobCard.id}`)}
                                                            className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                            title="Edit Job Card"
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {jobCard.job_number}
                                                </div>
                                                {jobCard.order_number && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        Order: {jobCard.order_number}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {jobCard.user?.utype === 'STORE' ? jobCard.supplier?.name : jobCard.store?.name}
                                                    {jobCard.linked_from_job_card_id && (
                                                        <span title="This job card is linked/merged from a PO job card" className="ml-2 align-middle text-blue-500">
                                                            <LinkIcon className="inline w-4 h-4" />
                                                        </span>
                                                    )}
                                                </div>
                                                {jobCard.user?.utype !== 'STORE' && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        #{jobCard.store?.storenumber}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {formatDate(jobCard.date)}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {jobCard.time_in} - {jobCard.time_out}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {jobCard.created_at ? `${formatDate(jobCard.created_at)} ${new Date(jobCard.created_at).toLocaleTimeString()}` : 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {jobCard.user?.name || 'Unassigned'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full
                                                    ${jobCard.closed
                                                        ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400'
                                                        : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400'
                                                    }`}
                                                >
                                                    {jobCard.closed ? 'Closed' : 'Open'}
                                                </span>
                                                {jobCardsWithATRItems[jobCard.id] && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400">
                                                        From ATR
                                                    </span>
                                                )}
                                                {jobCard.closed && jobCard.date_closed && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        {formatDateClosed(jobCard.date_closed)}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {jobCards.map((jobCard) => (
                                <div
                                    key={jobCard.id}
                                    className={`
                                        p-4 rounded-lg shadow transition-colors cursor-pointer hover:ring-2 hover:ring-indigo-400
                                        ${jobCard.closed
                                            ? 'bg-green-100 dark:bg-green-800/30'
                                            : 'bg-yellow-100 dark:bg-yellow-800/30'
                                        }
                                    `}
                                    onClick={() => navigate(jobCard.closed ? `/jobcards/view/${jobCard.id}` : `/jobcards/edit/${jobCard.id}`)}
                                    tabIndex={0}
                                    role="button"
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            navigate(jobCard.closed ? `/jobcards/view/${jobCard.id}` : `/jobcards/edit/${jobCard.id}`);
                                        }
                                    }}
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <div>
                                            <div className="font-bold text-lg text-gray-900 dark:text-white">{jobCard.job_number}</div>
                                            {jobCard.order_number && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Order: {jobCard.order_number}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className={`px-2 py-1 text-xs rounded-full
                                                ${jobCard.closed
                                                    ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400'
                                                    : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400'
                                                }`}
                                            >
                                                {jobCard.closed ? 'Closed' : 'Open'}
                                            </span>
                                            {jobCardsWithATRItems[jobCard.id] && (
                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400">
                                                    From ATR
                                                </span>
                                            )}
                                            {jobCard.linked_from_job_card_id && (
                                                <span title="This job card is linked/merged from a PO job card" className="mt-1 text-blue-500">
                                                    <LinkIcon className="inline w-4 h-4" />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                                        {jobCard.user?.utype === 'STORE' 
                                            ? `Supplier: ${jobCard.supplier?.name || 'N/A'}`
                                            : `Store: ${jobCard.store?.name + ' #' + jobCard.store?.storenumber}`
                                        }
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                                        Date: {formatDate(jobCard.date)}
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                                        Created: {jobCard.created_at ? `${formatDate(jobCard.created_at)} ${new Date(jobCard.created_at).toLocaleTimeString()}` : 'N/A'}
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                                        Assigned: {jobCard.user?.name || 'Unassigned'}
                                    </div>
                                    {jobCard.closed && jobCard.date_closed && (
                                        <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                                            Closed: {formatDateClosed(jobCard.date_closed)}
                                        </div>
                                    )}
                                    <div className="flex gap-2 mt-2">
                                        {jobCard.closed ? (
                                            <button
                                                onClick={() => navigate(`/jobcards/view/${jobCard.id}`)}
                                                className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                View
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => navigate(`/jobcards/edit/${jobCard.id}`)}
                                                className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                            >
                                                <Pencil className="w-4 h-4 mr-1" />
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add pagination controls */}
                    <div className="mt-6 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1); // Reset to first page when changing items per page
                                }}
                                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            >
                                <option value="10">10 per page</option>
                                <option value="25">25 per page</option>
                                <option value="50">50 per page</option>
                                <option value="100">100 per page</option>
                            </select>
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                Page {currentPage} of {totalPages} ({totalItems} items)
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}

            <CreateJobCardModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={handleCreateJobCard}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                itemType="job card"
                itemName={jobCardToDelete?.job_number || ''}
            />
        </div>
    );
} 